import React, { useMemo } from "react";
import { EdgeLabelRenderer, Position, getSmoothStepPath } from "@xyflow/react";
import { cn } from "@/utils/theme";
import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUIStore } from "@/workflow_api/store/uiStore";
import {
  NodeTypes,
  type EdgeData,
  type NodeData,
  type RefName,
} from "@/workflow_api/types/workflow";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";

interface WorkflowEdgeProps {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  style?: React.CSSProperties;
  data?: EdgeData;
  markerEnd?: string;
  selected?: boolean;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

export function WorkflowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  selected,
  source,
  target,
}: WorkflowEdgeProps) {
  const [isHovered, setIsHovered] = React.useState(false);

  const { nodeConnecting, flowId, getNodeById } = useWorkflowStore();
  const { setConnectionToSplit, setNodeCreatorOpen, isFlowBlocked } =
    useUIStore();
  const isConnecting = nodeConnecting === data?.source.id;

  const label = useMemo(() => {
    const sourceNode = getNodeById(data!.source.id!);
    const _data = sourceNode?.data as unknown as NodeData;
    if (_data?.connections && _data.connections[data!.source.handle!]) {
      return _data.connections[data!.source.handle!].display_name;
    }
  }, [data, getNodeById]);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    offset: isConnecting ? 0 : 20,
    centerX: (sourceX + targetX) / 2,
    centerY: (sourceY + targetY) / 2,
  });

  const getEdgeColor = () => {
    if (selected) return "var(--border-action)";
    return "var(--border-neutral)";
  };

  const getEdgeWidth = () => {
    if (selected) return 2;
    return 2;
  };

  const handleAddNodeBetween = () => {
    if (!source || !target || !data?.source.id || !data?.target.id) return;

    // Set the connection information for splitting
    setConnectionToSplit({
      edgeId: id,
      sourceNodeId: source,
      targetNodeId: target,
      sourceHandle: data.source.handle,
      targetHandle: data.target.handle,
    });

    // Open the node creator modal
    setNodeCreatorOpen(true);
  };

  const { mutateAsync: updateNode } = useUpdateNode(flowId);

  const onRemoveConnection = async () => {
    if (!data?.target.id || !data?.source.id) return;
    const targetNode = getNodeById(data?.target.id);
    if (!targetNode) return;
    const targetData = targetNode?.data as unknown as NodeData;

    const isMultiInputNode =
      targetData.type === NodeTypes.WAIT || targetData.type === NodeTypes.MERGE;
    const handleMatch = data.target.handle?.match(
      /^(?:input|merge-input)-(\d+)$/,
    );

    if (isMultiInputNode && handleMatch) {
      const isMerge = targetData.type === NodeTypes.MERGE;
      const inputTypeFilter = isMerge ? "MERGE_ENTRY" : "THREAD";

      const updatedInputs = targetData.inputs?.map((input) => {
        if (input.type !== inputTypeFilter) return input;

        if (
          input.id === data.target.handle ||
          (isMerge &&
            `merge-input-${targetData.inputs.filter((i) => i.type === "MERGE_ENTRY").indexOf(input) + 1}` ===
              data.target.handle) ||
          (!isMerge &&
            `input-${targetData.inputs.filter((i) => i.type === "THREAD").indexOf(input) + 1}` ===
              data.target.handle)
        ) {
          return {
            ...input,
            source_connections: (input.source_connections || []).filter(
              (conn) =>
                !(
                  conn.source_node_id === data.source.id &&
                  (conn.field?.id === data.source.handle ||
                    conn.source_node_output_id === data.source.handle)
                ),
            ),
          };
        }
        return input;
      }) as NodeData["inputs"];

      if (!updatedInputs) return;

      // Build a map of existing edges for source_handler lookup
      const existingEdgesMap = new Map<string, string>();
      if (targetData.edges && Array.isArray(targetData.edges)) {
        targetData.edges.forEach((edge) => {
          if (edge.target_handler && edge.source_handler && edge.source) {
            existingEdgesMap.set(
              `${edge.source}-${edge.target_handler}`,
              edge.source_handler,
            );
          }
        });
      }

      const filteredInputsOnly = updatedInputs.filter(
        (input) => input.type === inputTypeFilter,
      );
      const apiEdges = filteredInputsOnly.flatMap((input, idx) => {
        if (!Array.isArray(input.source_connections)) return [];
        const targetHandler = isMerge
          ? `merge-input-${idx + 1}`
          : `input-${idx + 1}`;
        return input.source_connections.map((connection) => ({
          source: connection.source_node_id,
          source_handler:
            connection.field?.id ||
            connection.source_node_output_id ||
            existingEdgesMap.get(
              `${connection.source_node_id}-${targetHandler}`,
            ) ||
            "source",
          target_handler: targetHandler,
          target_input_name: input.name,
        }));
      });

      updateNode({
        node_id: data.target.id,
        node: {
          display_name: targetData.displayName,
          flow_id: flowId,
          ref_name: targetData.type as RefName,
          order: targetData.order,
          position_x: targetNode.position.x,
          position_y: targetNode.position.y,
          type: targetData.node_type,
          edge: null,
          edges: apiEdges,
          inputs: updatedInputs,
        },
      });
      return;
    }

    // Default handling for non-Wait nodes
    updateNode({
      node_id: data.target.id,
      node: {
        display_name: targetData.displayName,
        flow_id: flowId,
        ref_name: targetData.type as RefName,
        order: targetData.order,
        position_x: targetNode.position.x,
        position_y: targetNode.position.y,
        type: targetData.node_type,
        edge: null,
      },
    });
  };

  return (
    <>
      <path
        id={id}
        style={{
          stroke: getEdgeColor(),
          strokeWidth: getEdgeWidth(),
          fill: "none",
        }}
        className={cn(
          "react-flow__edge-path peer",
          // isAnimated && "animated"
        )}
        d={edgePath}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        markerEnd={markerEnd}
      />
      <EdgeLabelRenderer>
        {!isFlowBlocked && (
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 12,
              pointerEvents: "all",
              zIndex: 41,
            }}
            className={cn(
              "no-drag",
              "opacity-0 hover:opacity-100 transition-opacity duration-200 bg-neutral-object",
              isHovered && "opacity-100",
              "peer-hover:opacity-100",
              "flex",
              "px-1 flex items-center justify-between gap-1",
            )}
          >
            <Button
              variant="quiet"
              size="icon"
              className="size-7 rounded-[4px] no-drag"
              aria-label="Add node between"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddNodeBetween();
              }}
            >
              <Icon type="far" name="plus" />
            </Button>
            <Button
              variant="quiet"
              size="icon"
              className="size-7 rounded-[4px] no-drag"
              aria-label="Delete connection"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemoveConnection();
              }}
            >
              <Icon type="far" name="trash" />
            </Button>
          </div>
        )}
        {label && (
          <div
            style={{
              position: "absolute",
              transform: `translate(0%, -50%) translate(${sourceX}px,${sourceY}px)`,
              fontSize: 12,
              pointerEvents: "all",
              zIndex: 50,
            }}
            className="no-drag nopan bg-neutral-object rounded-lg w-auto border border-neutral"
          >
            <TooltipWrapper title={label}>
              <div className="edge-label min-w-[40px] max-w-[148px] px-1.5 text-body-loud whitespace-nowrap truncate">
                {label}
              </div>
            </TooltipWrapper>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}

const TooltipWrapper = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={4}
        className="max-w-[240px] break-all"
      >
        {title}
      </TooltipContent>
    </Tooltip>
  );
};
